<template>
  <div class="wp" @mouseleave="clearHover">
    <!-- Legend doubles as the filter: click toggles a source, hover highlights
         it. Identity is never colour-alone — every entry is also named. -->
    <div class="wp-legend">
      <button
        v-for="s in allSources"
        :key="s"
        type="button"
        class="wp-leg"
        :class="{ off: isHidden(s), dim: hoverSource && hoverSource !== s }"
        :title="$t('s4c-utilization.weekly.toggle-hint')"
        @click="toggle(s)"
        @mouseenter="hoverSource = s"
        @mouseleave="hoverSource = null"
      >
        <i
          :style="{
            background: isHidden(s) ? 'transparent' : colorOf(s),
            borderColor: colorOf(s),
          }"
        />
        {{ s }}
        <span class="wp-leg-eur">{{ kEur(totalOf(s)) }}</span>
      </button>
    </div>

    <!-- ---------- panel 1: revenue, stacked by source ---------- -->
    <div class="wp-panel">
      <div class="wp-title">
        {{ $t('s4c-utilization.weekly.revenue') }}
        <span class="wp-unit">EUR</span>
      </div>
      <div class="wp-plot" :style="{ height: `${H1}px` }">
        <div class="wp-yaxis">
          <span v-for="t in axisLabels(maxRevenue)" :key="`rl${t}`">
            {{ kEur(t) }}
          </span>
        </div>
        <svg
          :viewBox="`0 0 ${W} ${H1}`"
          class="wp-svg"
          preserveAspectRatio="none"
          @mousemove="onMove($event, $el)"
        >
          <line
            v-for="t in ticks(maxRevenue)"
            :key="`rg${t}`"
            x1="0"
            :x2="W"
            :y1="y(t, maxRevenue, H1)"
            :y2="y(t, maxRevenue, H1)"
            class="grid"
          />
          <rect
            v-if="hoverWeek !== null"
            :x="bx(hoverWeek) - 1"
            :width="bw + 2"
            y="0"
            :height="H1"
            class="cross"
          />
          <template v-for="(w, i) in weeks">
            <rect
              v-for="seg in stack(w)"
              :key="`${w.week}-${seg.source}`"
              :x="bx(i)"
              :width="bw"
              :y="y(seg.top, maxRevenue, H1)"
              :height="segHeight(seg, maxRevenue, H1)"
              :fill="colorOf(seg.source)"
              :opacity="segOpacity(seg.source)"
              rx="1"
            />
          </template>
        </svg>
      </div>
    </div>

    <!-- ---------- panel 2: capacity ---------- -->
    <div class="wp-panel">
      <div class="wp-title">
        {{ $t('s4c-utilization.weekly.capacity') }}
        <span class="wp-unit">{{ $t('s4c-utilization.weekly.hours') }}</span>
      </div>
      <div class="wp-plot" :style="{ height: `${H2}px` }">
        <div class="wp-yaxis">
          <span v-for="t in axisLabels(maxCapacity)" :key="`cl${t}`">
            {{ Math.round(t) }}
          </span>
        </div>
        <svg
          :viewBox="`0 0 ${W} ${H2}`"
          class="wp-svg"
          preserveAspectRatio="none"
          @mousemove="onMove($event, $el)"
        >
          <line
            v-for="t in ticks(maxCapacity)"
            :key="`cg${t}`"
            x1="0"
            :x2="W"
            :y1="y(t, maxCapacity, H2)"
            :y2="y(t, maxCapacity, H2)"
            class="grid"
          />
          <rect
            v-if="hoverWeek !== null"
            :x="bx(hoverWeek) - 1"
            :width="bw + 2"
            y="0"
            :height="H2"
            class="cross"
          />
          <rect
            v-for="(w, i) in weeks"
            :key="`cap-${w.week}`"
            :x="bx(i)"
            :width="bw"
            :y="y(w.capacity_h, maxCapacity, H2)"
            :height="Math.max(0.5, H2 - y(w.capacity_h, maxCapacity, H2))"
            class="cap"
            rx="1"
          />
          <polyline :points="bookedLine" class="booked" />
        </svg>
      </div>
    </div>

    <!-- ---------- panel 3: utilization ---------- -->
    <div class="wp-panel">
      <div class="wp-title">
        {{ $t('s4c-utilization.weekly.utilization') }}
        <span class="wp-unit">%</span>
      </div>
      <div class="wp-plot" :style="{ height: `${H3}px` }">
        <div class="wp-yaxis">
          <span v-for="t in axisLabels(1)" :key="`ul${t}`">
            {{ Math.round(t * 100) }}
          </span>
        </div>
        <svg
          :viewBox="`0 0 ${W} ${H3}`"
          class="wp-svg"
          preserveAspectRatio="none"
          @mousemove="onMove($event, $el)"
        >
          <!-- realistic ceiling, not 100%: drive time is inside the measure and
               ~20% of Einsätze already hit the per-day cap -->
          <rect
            x="0"
            :width="W"
            :y="y(0.9, 1, H3)"
            :height="y(0.85, 1, H3) - y(0.9, 1, H3)"
            class="target"
          />
          <line
            v-for="t in [0.25, 0.5, 0.75, 1]"
            :key="`ug${t}`"
            x1="0"
            :x2="W"
            :y1="y(t, 1, H3)"
            :y2="y(t, 1, H3)"
            class="grid"
          />
          <rect
            v-if="hoverWeek !== null"
            :x="bx(hoverWeek) - 1"
            :width="bw + 2"
            y="0"
            :height="H3"
            class="cross"
          />
          <polyline :points="utilLine" class="util" />
          <circle
            v-for="(p, i) in utilPoints"
            :key="`up${i}`"
            :cx="p.x"
            :cy="p.y"
            r="3"
            class="util-dot"
          />
        </svg>
      </div>
    </div>

    <!-- shared x-axis, drawn once for all three panels -->
    <div class="wp-x">
      <span
        v-for="(w, i) in weeks"
        :key="`x${i}`"
        :style="xStyle()"
        :class="{ on: hoverWeek === i }"
      >
        {{ w.week.slice(-3) }}
      </span>
    </div>

    <!-- one tooltip for all three panels: they share a week, so a single
         readout answers "what happened in this week" across all measures -->
    <div v-if="hoverWeek !== null" class="wp-tip" :style="tipStyle">
      <div class="wp-tip-h">{{ weeks[hoverWeek].week }}</div>
      <div
        v-for="seg in tipRows"
        :key="seg.source"
        class="wp-tip-r"
        :class="{ hi: hoverSource === seg.source }"
      >
        <i :style="{ background: colorOf(seg.source) }" />
        <span class="l">{{ seg.source }}</span>
        <span class="v">{{ eur(seg.value) }}</span>
      </div>
      <div class="wp-tip-r tot">
        <span class="l">{{ $t('s4c-utilization.weekly.total') }}</span>
        <span class="v">{{ eur(visibleTotal(weeks[hoverWeek])) }}</span>
      </div>
      <div class="wp-tip-sep" />
      <div class="wp-tip-r">
        <span class="l">{{ $t('s4c-utilization.weekly.capacity-short') }}</span>
        <span class="v">
          {{ Math.round(weeks[hoverWeek].capacity_h) }} h ·
          {{ weeks[hoverWeek].avg_heroes.toFixed(1) }}
          {{ $t('s4c-utilization.weekly.heroes') }}
        </span>
      </div>
      <div class="wp-tip-r">
        <span class="l">{{ $t('s4c-utilization.weekly.booked-short') }}</span>
        <span class="v">{{ Math.round(weeks[hoverWeek].booked_h) }} h</span>
      </div>
      <div class="wp-tip-r">
        <span class="l">{{ $t('s4c-utilization.weekly.utilization') }}</span>
        <span class="v">
          {{
            weeks[hoverWeek].utilization === null
              ? '—'
              : `${Math.round(100 * weeks[hoverWeek].utilization)}%`
          }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Weekly revenue, hero capacity and utilization on one shared week axis.
 *
 * Deliberately **three stacked panels, not one chart with two y-axes.** The
 * measures are euros, hours and a ratio; on a dual axis any pair of them can be
 * made to look correlated or uncorrelated purely by choosing the scales, which
 * is exactly the reading the chart is meant to support. Aligned panels give the
 * same "read down the column" comparison and cannot be tuned into a conclusion.
 *
 * The capacity panel carries booked hours as a line inside the capacity bars —
 * same unit, so that one genuinely is an overlay.
 *
 * The legend is also the filter: click toggles a source, hover highlights it in
 * the stack. Hiding a source rescales the revenue axis on purpose — the point of
 * hiding one is to read the rest, and holding the old maximum would flatten them
 * into the floor.
 */

// Fixed hue order. Assigned by source identity, never by rank, so toggling a
// source cannot repaint the survivors.
const COLORS = {
  'GID · Partner extern': '#2a78d6',
  'GID · Partner S4C (intern)': '#1baf7a',
  'GID · ohne Partner': '#9a9892',
  'Charging Success': '#eb6834',
  Deployment: '#c2417e',
  'Rapid Deployment': '#eda100',
  Kalkulation: '#3aa0a8',
  'Field Service': '#8a6d3b',
  'ohne Team': '#c8c6c0',
};
const FALLBACK = '#c8c6c0';

// Off by default. Kalkulation is quoting work, not delivered business, so it
// competes with the operational sources on a chart about delivery. Still
// toggleable — it is hidden, not dropped.
const HIDDEN_BY_DEFAULT = ['Kalkulation'];

export default {
  name: 'WeeklyPanels',
  props: {
    weeks: { type: Array, required: true },
    sources: { type: Array, required: true },
  },
  // The SVGs stretch horizontally (`preserveAspectRatio="none"`), so no text may
  // live inside them — it would be distorted by the same factor. Tick labels are
  // an HTML gutter to the left of each plot, and the shared x-axis is indented
  // by that gutter's width so the columns line up.
  data() {
    return {
      W: 900,
      H1: 170,
      H2: 130,
      H3: 120,
      hidden: [...HIDDEN_BY_DEFAULT],
      hoverSource: null,
      hoverWeek: null,
      tipX: 0,
      tipY: 0,
    };
  },
  computed: {
    allSources() {
      // Every source that carries money in this window, whether or not it is
      // currently shown — a hidden source must stay in the legend or there is
      // no way to bring it back.
      return this.sources.filter((s) => s.eur > 0).map((s) => s.label);
    },
    activeSources() {
      return this.allSources.filter((s) => !this.isHidden(s));
    },
    maxRevenue() {
      return Math.max(1, ...this.weeks.map((w) => this.visibleTotal(w)));
    },
    maxCapacity() {
      return Math.max(1, ...this.weeks.map((w) => w.capacity_h || 0));
    },
    bw() {
      const n = Math.max(1, this.weeks.length);
      return Math.max(2, this.W / n - 2);
    },
    utilPoints() {
      return this.weeks
        .map((w, i) => ({
          week: w.week,
          v: w.utilization,
          x: this.bx(i) + this.bw / 2,
          y: this.y(w.utilization || 0, 1, this.H3),
        }))
        .filter((p) => p.v !== null && p.v !== undefined);
    },
    utilLine() {
      return this.utilPoints.map((p) => `${p.x},${p.y}`).join(' ');
    },
    bookedLine() {
      return this.weeks
        .map(
          (w, i) =>
            `${this.bx(i) + this.bw / 2},${this.y(
              w.booked_h || 0,
              this.maxCapacity,
              this.H2,
            )}`,
        )
        .join(' ');
    },
    tipRows() {
      if (this.hoverWeek === null) return [];
      const w = this.weeks[this.hoverWeek];
      return this.activeSources
        .map((source) => ({ source, value: w.revenue?.[source] || 0 }))
        .filter((r) => r.value > 0)
        .sort((a, b) => b.value - a.value);
    },
    tipStyle() {
      return { left: `${this.tipX}px`, top: `${this.tipY}px` };
    },
  },
  methods: {
    colorOf(source) {
      return COLORS[source] || FALLBACK;
    },
    isHidden(source) {
      return this.hidden.includes(source);
    },
    toggle(source) {
      const at = this.hidden.indexOf(source);
      // Vue 2 cannot see index assignment on an array, so replace it.
      this.hidden =
        at === -1
          ? [...this.hidden, source]
          : this.hidden.filter((s) => s !== source);
    },
    totalOf(source) {
      return this.sources.find((s) => s.label === source)?.eur || 0;
    },
    visibleTotal(week) {
      return this.activeSources.reduce(
        (a, s) => a + (week.revenue?.[s] || 0),
        0,
      );
    },
    segOpacity(source) {
      // Legend hover dims everything else rather than brightening one series,
      // so the highlight cannot be mistaken for a different colour.
      if (!this.hoverSource) return 1;
      return this.hoverSource === source ? 1 : 0.25;
    },
    onMove(event, root) {
      const svg = event.currentTarget;
      const box = svg.getBoundingClientRect();
      const n = Math.max(1, this.weeks.length);
      const i = Math.floor(((event.clientX - box.left) / box.width) * n);
      this.hoverWeek = Math.min(n - 1, Math.max(0, i));
      const host = root.getBoundingClientRect();
      // Flip the tooltip to the left of the cursor near the right edge so it
      // never forces the card to scroll horizontally.
      const raw = event.clientX - host.left + 14;
      this.tipX = raw > host.width - 230 ? raw - 244 : raw;
      this.tipY = event.clientY - host.top + 14;
    },
    clearHover() {
      this.hoverWeek = null;
      this.hoverSource = null;
    },
    bx(i) {
      const n = Math.max(1, this.weeks.length);
      return (i * this.W) / n + 1;
    },
    xStyle() {
      const n = Math.max(1, this.weeks.length);
      return { width: `${100 / n}%` };
    },
    y(value, max, h) {
      return h - (Math.max(0, value) / max) * h;
    },
    segHeight(seg, max, h) {
      // Guard against negative/sub-pixel rects: a segment thinner than the gap
      // would otherwise render with a negative height and log an SVG error.
      return Math.max(0.5, ((seg.top - seg.bottom) / max) * h);
    },
    // Top-to-bottom, so a flex column with space-between puts `max` at the top
    // of the plot and 0 on the baseline.
    axisLabels(max) {
      return [max, max * 0.75, max * 0.5, max * 0.25, 0];
    },
    stack(week) {
      // Stacked in the fixed legend order so a week with a missing source does
      // not reshuffle the colours of the ones present.
      const out = [];
      let acc = 0;
      this.activeSources.forEach((source) => {
        const value = week.revenue?.[source] || 0;
        if (value <= 0) return;
        out.push({ source, value, bottom: acc, top: acc + value });
        acc += value;
      });
      return out;
    },
    ticks(max) {
      return [max * 0.25, max * 0.5, max * 0.75, max];
    },
    eur(v) {
      return `${Math.round(v).toLocaleString('de-DE')} €`;
    },
    kEur(v) {
      return v >= 1000 ? `${Math.round(v / 1000)}k` : String(Math.round(v));
    },
  },
};
</script>

<style lang="scss" scoped>
.wp {
  position: relative;
  border: 1px solid var(--s4c-border);
  border-radius: 6px;
  background: var(--s4c-surface-1);
  padding: 10px 12px 6px;
}

.wp-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 6px;
  margin-bottom: 8px;
}

.wp-leg {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 1px 6px 1px 4px;
  font-size: 11px;
  color: var(--s4c-ink-2);
  cursor: pointer;
  transition: opacity 0.12s;

  i {
    display: inline-block;
    width: 9px;
    height: 9px;
    border-radius: 2px;
    border: 1.5px solid transparent;
  }

  .wp-leg-eur {
    color: var(--s4c-ink-3);
    font-variant-numeric: tabular-nums;
  }

  &:hover {
    border-color: var(--s4c-border);
  }
  &.off {
    color: var(--s4c-ink-3);
    text-decoration: line-through;
  }
  &.dim {
    opacity: 0.4;
  }
}

.wp-panel {
  margin-bottom: 10px;
}

.wp-title {
  font-size: 11px;
  font-weight: 600;
  color: var(--s4c-ink-2);
  margin-bottom: 2px;

  .wp-unit {
    font-weight: 400;
    color: var(--s4c-ink-3);
    margin-left: 5px;
  }
}

.wp-plot {
  display: flex;
  align-items: stretch;
}

.wp-yaxis {
  flex: 0 0 46px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  padding-right: 6px;
  font-size: 10px;
  line-height: 1;
  color: var(--s4c-ink-3);
  font-variant-numeric: tabular-nums;
}

.wp-svg {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  display: block;
}

.grid {
  stroke: var(--s4c-border);
  stroke-width: 1;
  vector-effect: non-scaling-stroke;
}

.cross {
  fill: var(--s4c-ink-1);
  opacity: 0.06;
}

.cap {
  fill: #cde2fb;
}

.booked {
  fill: none;
  stroke: #2d69b4;
  stroke-width: 2;
  vector-effect: non-scaling-stroke;
}

.util {
  fill: none;
  stroke: #1baf7a;
  stroke-width: 2;
  vector-effect: non-scaling-stroke;
}

.util-dot {
  fill: #1baf7a;
}

.target {
  fill: #1baf7a;
  opacity: 0.1;
}

.wp-x {
  display: flex;
  font-size: 10px;
  color: var(--s4c-ink-3);
  border-top: 1px solid var(--s4c-border);
  padding-top: 3px;
  // Same width as .wp-yaxis so week columns line up with the bars above them.
  padding-left: 46px;

  span {
    text-align: center;
    overflow: hidden;

    &.on {
      color: var(--s4c-ink-1);
      font-weight: 700;
    }
  }
}

.wp-tip {
  position: absolute;
  z-index: 5;
  pointer-events: none;
  min-width: 200px;
  max-width: 230px;
  background: var(--s4c-surface-1);
  border: 1px solid var(--s4c-border);
  border-radius: 5px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.13);
  padding: 7px 9px;
  font-size: 11px;

  .wp-tip-h {
    font-weight: 700;
    margin-bottom: 4px;
    color: var(--s4c-ink-1);
  }

  .wp-tip-r {
    display: flex;
    align-items: center;
    gap: 5px;
    line-height: 1.55;

    i {
      display: inline-block;
      width: 8px;
      height: 8px;
      border-radius: 2px;
      flex: 0 0 8px;
    }
    .l {
      flex: 1 1 auto;
      color: var(--s4c-ink-2);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .v {
      font-variant-numeric: tabular-nums;
      color: var(--s4c-ink-1);
    }
    &.hi {
      background: rgba(42, 120, 214, 0.1);
      border-radius: 3px;
    }
    &.tot {
      font-weight: 700;
      border-top: 1px solid var(--s4c-border);
      margin-top: 3px;
      padding-top: 3px;
    }
  }

  .wp-tip-sep {
    height: 1px;
    background: var(--s4c-border);
    margin: 5px 0;
  }
}
</style>
